import React, { useState, useEffect, useMemo } from "react";
import { getTickets, createTicket } from "../services/ticketService";
import { getClientMachines } from "../controllers/machineController";
import { useBranch } from "../context/BranchContext";
import "./ComplaintTicketTab.css";

export default function ComplaintTicketTab() {
  const [activeTab, setActiveTab] = useState("new"); // "new" or "list"
  const { selectedBranch, machineToBranchMap, meterIdToBranchMap } = useBranch();
  
  // Data loading states
  const [tickets, setTickets] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active user session
  const username = useMemo(() => {
    let user = null;
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        user = JSON.parse(storedUser);
      }
    } catch (e) {
      user = null;
    }
    return (
      localStorage.getItem("username") ||
      user?.username ||
      user?.userName ||
      user?.email ||
      ""
    );
  }, []);

  const clientName = useMemo(() => {
    let name = username;
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        name = parsed.clientName || parsed.client_name || parsed.company || username;
      }
    } catch (e) {
      // ignore
    }
    return name || "Client";
  }, [username]);

  const getMachineBranch = useMemo(() => {
    return (machine) =>
      machineToBranchMap[machine.machine_name] ||
      meterIdToBranchMap[String(machine.meter_id)] ||
      null;
  }, [machineToBranchMap, meterIdToBranchMap]);

  const scopedMachines = useMemo(() => {
    if (selectedBranch === "All") {
      return machines;
    }

    return machines.filter((machine) => {
      const branchName = getMachineBranch(machine);
      return machine.machine_name === selectedBranch || branchName === selectedBranch;
    });
  }, [machines, selectedBranch, getMachineBranch]);

  // Form states
  const [formSubject, setFormSubject] = useState("");
  const [formCategory, setFormCategory] = useState("Missing Data");
  const [formMachineIndex, setFormMachineIndex] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formSuccess, setFormSuccess] = useState(null);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Selection states
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Load ticket and machine data
  const loadData = async () => {
    if (!username) return;
    setLoading(true);
    setError(null);
    try {
      const fetchedTickets = await getTickets(username);
      setTickets(fetchedTickets);
      
      const machinesResponse = await getClientMachines(username);
      setMachines(machinesResponse.machines || []);
    } catch (err) {
      console.error("Error loading tickets/machines data:", err);
      setError("Failed to retrieve system settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);


  // Derived stats
  const stats = useMemo(() => {
    const total = tickets.length;
    const open = tickets.filter(t => t.status === "Open").length;
    const progress = tickets.filter(t => t.status === "In Progress").length;
    const resolved = tickets.filter(t => t.status === "Resolved" || t.status === "Closed").length;
    return { total, open, progress, resolved };
  }, [tickets]);

  // Submit Complaint Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!formSubject.trim()) {
      setFormError("Please enter a subject / title for the complaint.");
      return;
    }
    if (!formDescription.trim()) {
      setFormError("Please enter a detailed description of the issue.");
      return;
    }

    setSubmitting(true);
    
    // Resolve machine metadata if selected
    let meterId = "";
    let machineName = "";
    if (formMachineIndex !== "") {
      const m = machines[Number(formMachineIndex)];
      if (m) {
        meterId = m.meter_id || "";
        machineName = m.machine_name || "";
      }
    }

    try {
      const newTkt = await createTicket({
        subject: formSubject.trim(),
        description: formDescription.trim(),
        client_name: clientName,
        category: formCategory,
        priority: "High",
        meter_id: meterId || undefined,
        machine_name: machineName || undefined,
        email: formEmail.trim() || undefined,
        phone_number: formPhone.trim() || undefined,
      }, username);

      setTickets(prev => [newTkt, ...prev]);
      setFormSuccess("Your ticket was successfully registered. Our support team has been notified.");
      
      // Reset form fields
      setFormSubject("");
      setFormMachineIndex("");
      setFormEmail("");
      setFormPhone("");
      setFormDescription("");
      setFormCategory("Missing Data");
      
      // Auto switch tab to list to see the created ticket after a small delay
      setTimeout(() => {
        setFormSuccess(null);
        setActiveTab("list");
        setSelectedTicket(newTkt);
      }, 3000);
    } catch (err) {
      console.error(err);
      setFormError("Failed to submit ticket. Please verify connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };


  // Format Helper for Date
  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      const date = new Date(iso);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="complaint-tab-container">
      {/* Header */}
      <div className="complaint-header">
        <div className="complaint-title-area">
          <h2>Complaint Support Tickets</h2>
          <p>Register complaints regarding machine metrics, data gaps, or billing, and view status</p>
        </div>
        
        <div className="complaint-view-toggle">
          <button 
            type="button" 
            className={`toggle-btn ${activeTab === "new" ? "active" : ""}`}
            onClick={() => { setActiveTab("new"); setFormSuccess(null); setFormError(null); }}
          >
            Submit Complaint
          </button>
          <button 
            type="button" 
            className={`toggle-btn ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
          >
            My Tickets ({tickets.length})
          </button>
        </div>
      </div>

      {/* Stats counters */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem", marginBottom: "2rem" }}>
        <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Total Filed</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1e293b", marginTop: "0.25rem" }}>{stats.total}</div>
        </div>
        <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: "8px", border: "1px solid #e2e8f0", borderLeft: "4px solid #3b82f6" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Open</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1e293b", marginTop: "0.25rem" }}>{stats.open}</div>
        </div>
        <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: "8px", border: "1px solid #e2e8f0", borderLeft: "4px solid #f59e0b" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>In Progress</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1e293b", marginTop: "0.25rem" }}>{stats.progress}</div>
        </div>
        <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: "8px", border: "1px solid #e2e8f0", borderLeft: "4px solid #10b981" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Resolved</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1e293b", marginTop: "0.25rem" }}>{stats.resolved}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "4rem" }}>
          <div style={{ width: "2rem", height: "2rem", border: "3px solid #1ba08e", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
          <p style={{ marginTop: "1rem", color: "#64748b" }}>Loading tickets & machines...</p>
        </div>
      ) : error ? (
        <div className="complaint-card text-center" style={{ color: "#dc2626", padding: "2rem" }}>
          <svg style={{ width: "32px", height: "32px" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 style={{ marginTop: "1rem", margin: "0.5rem 0" }}>System Error</h3>
          <p>{error}</p>
        </div>
      ) : (
        <>
          {/* TAB 1: Submit Form */}
          {activeTab === "new" && (
            <div className="complaint-card" style={{ maxWidth: "680px", margin: "0 auto" }}>
              <h3 className="complaint-card-title">Register a Support Complaint</h3>
              
              <form onSubmit={handleSubmit}>
                {formSuccess && <div className="alert-message success">{formSuccess}</div>}
                {formError && <div className="alert-message error">{formError}</div>}

                <div className="complaint-form-group">
                  <label>Subject / Summary</label>
                  <input
                    type="text"
                    className="complaint-input"
                    placeholder="e.g. Meter M-928 stopped communicating"
                    value={formSubject}
                    onChange={(e) => setFormSubject(e.target.value)}
                  />
                </div>

                <div className="complaint-form-group">
                  <label>Category</label>
                  <select
                    className="complaint-select"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                  >
                    <option value="Missing Data">Missing Data</option>
                    <option value="Offline Meter">Offline Meter</option>
                    <option value="Inaccurate Readings">Inaccurate Readings</option>
                    <option value="Billing">Billing Issue</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="complaint-form-group">
                  <label>Affected Machine/Meter (Optional)</label>
                  <select
                    className="complaint-select"
                    value={formMachineIndex}
                    onChange={(e) => setFormMachineIndex(e.target.value)}
                  >
                    <option value="">-- Select Affected Device --</option>
                    {scopedMachines.map((m, idx) => (
                      <option key={idx} value={idx}>
                        {m.machine_name} (ID: {m.meter_id})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  <div className="complaint-form-group">
                    <label>Contact Email (Optional)</label>
                    <input
                      type="email"
                      className="complaint-input"
                      placeholder="e.g. contact@domain.com"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                    />
                    <small style={{ color: "#64748b", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                      Auto-fills from profile if blank
                    </small>
                  </div>

                  <div className="complaint-form-group">
                    <label>Contact Phone (Optional)</label>
                    <input
                      type="tel"
                      className="complaint-input"
                      placeholder="e.g. +92 300 1234567"
                      value={formPhone}
                      onChange={(e) => setFormPhone(e.target.value)}
                    />
                    <small style={{ color: "#64748b", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                      Auto-fills from profile if blank
                    </small>
                  </div>
                </div>

                <div className="complaint-form-group">
                  <label>Details / Description of Issue</label>
                  <textarea
                    className="complaint-textarea"
                    placeholder="Please explain the issue. Mention specific dates/times and readings if relevant..."
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                  />
                </div>

                <button 
                  type="submit" 
                  className="complaint-submit-btn"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Support Request"}
                </button>
              </form>
            </div>
          )}

          {/* TAB 2: List & Details Split */}
          {activeTab === "list" && (
            <div className="complaint-grid">
              
              {/* Ticket list card */}
              <div className="complaint-card" style={{ padding: "1rem" }}>
                <h3 className="complaint-card-title" style={{ padding: "0 0.5rem 0.5rem 0.5rem", marginBottom: "0.75rem" }}>My Support Tickets</h3>
                
                {tickets.length === 0 ? (
                  <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8" }}>
                    <p>No complaints registered yet.</p>
                  </div>
                ) : (
                  <div className="tickets-list-wrapper">
                    <table className="tickets-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Subject</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((t) => (
                          <tr
                            key={t.id}
                            className={`ticket-item-row ${selectedTicket?.id === t.id ? "selected" : ""}`}
                            onClick={() => setSelectedTicket(t)}
                          >
                            <td className="ticket-id">{t.id}</td>
                            <td>
                              <div className="ticket-subject">{t.subject}</div>
                              <div className="ticket-desc-trunc">{t.description}</div>
                              {(t.email || t.phone_number) && (
                                <div style={{ fontSize: "0.75rem", color: "#0d9488", marginTop: "3px" }}>
                                  {[t.email, t.phone_number].filter(Boolean).join(" • ")}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className={`ticket-badge badge-${t.status.replace(/\s+/g, "").toLowerCase()}`}>
                                {t.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Ticket Details Panel */}
              <div className="complaint-card">
                {selectedTicket ? (
                  <div className="ticket-detail-panel">
                    <div className="detail-header">
                      <div>
                        <h3>{selectedTicket.subject}</h3>
                        <div className="detail-header-meta">
                          <span className="ticket-id">{selectedTicket.id}</span>
                          <span className={`ticket-badge badge-${selectedTicket.status.replace(/\s+/g, "").toLowerCase()}`}>
                            {selectedTicket.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="detail-body-desc">
                      {selectedTicket.description}
                    </div>

                    <div className="detail-meta-grid">
                      <div className="detail-meta-item">
                        <span className="detail-meta-label">Category</span>
                        <span className="detail-meta-val">{selectedTicket.category}</span>
                      </div>
                      <div className="detail-meta-item">
                        <span className="detail-meta-label">Date Submitted</span>
                        <span className="detail-meta-val">{formatDate(selectedTicket.created_at)}</span>
                      </div>
                      {selectedTicket.email && (
                        <div className="detail-meta-item">
                          <span className="detail-meta-label">Contact Email</span>
                          <span className="detail-meta-val">{selectedTicket.email}</span>
                        </div>
                      )}
                      {selectedTicket.phone_number && (
                        <div className="detail-meta-item">
                          <span className="detail-meta-label">Contact Phone</span>
                          <span className="detail-meta-val">{selectedTicket.phone_number}</span>
                        </div>
                      )}
                      {selectedTicket.meter_id && (
                        <div className="detail-meta-item">
                          <span className="detail-meta-label">Meter ID</span>
                          <span className="detail-meta-val" style={{ fontFamily: "monospace" }}>{selectedTicket.meter_id}</span>
                        </div>
                      )}
                      {selectedTicket.machine_name && (
                        <div className="detail-meta-item">
                          <span className="detail-meta-label">Machine Name</span>
                          <span className="detail-meta-val">{selectedTicket.machine_name}</span>
                        </div>
                      )}
                    </div>

                  </div>
                ) : (
                  <div className="no-ticket-selected">
                    <svg style={{ width: "48px", height: "48px", color: "#cbd5e1" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                    <p>Select a ticket from the list to view timeline details and communicate with support.</p>
                  </div>
                )}
              </div>

            </div>
          )}
        </>
      )}
    </div>
  );
}
